import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getTopWinnersStart,
  getHighWinnersStart,
  getLuckyWinnersStart
} from 'store/redux-slices/casinoMenu'

const WINNERS_FETCH_LIMIT = 10

const actionByFilter = {
  all: getTopWinnersStart,
  high: getHighWinnersStart,
  lucky: getLuckyWinnersStart
}

/**
 * Custom hook to fetch and manage top performance winners data.
 * Each dataset is fetched lazily — the first time its tab is selected
 * during the component's lifetime (tracked per mount).
 *
 * @param {'all'|'high'|'lucky'} activeFilter Currently selected tab
 * @returns {{
 *   allWinners: Array,
 *   highWinners: Array,
 *   luckyWinners: Array,
 *   isLoading: boolean
 * }} Winner datasets plus the loading state of the active tab
 */
const useTopPerformance = (activeFilter) => {
  const dispatch = useDispatch()
  const fetchedFiltersRef = useRef(new Set())

  const allWinners = useSelector(state => state?.casinoMenu?.topWinners || [])
  const highWinners = useSelector(state => state?.casinoMenu?.highWinners || [])
  const luckyWinners = useSelector(state => state?.casinoMenu?.luckyWinners || [])
  const topWinnersLoading = useSelector(state => state?.casinoMenu?.topWinnersLoading)
  const highWinnersLoading = useSelector(state => state?.casinoMenu?.highWinnersLoading)
  const luckyWinnersLoading = useSelector(state => state?.casinoMenu?.luckyWinnersLoading)

  useEffect(() => {
    const action = actionByFilter[activeFilter]
    if (!action || fetchedFiltersRef.current.has(activeFilter)) return
    fetchedFiltersRef.current.add(activeFilter)
    dispatch(action({ limit: WINNERS_FETCH_LIMIT }))
  }, [activeFilter, dispatch])

  const loadingByFilter = {
    all: topWinnersLoading,
    high: highWinnersLoading,
    lucky: luckyWinnersLoading
  }

  return {
    allWinners: Array.isArray(allWinners) ? allWinners : [],
    highWinners: Array.isArray(highWinners) ? highWinners : [],
    luckyWinners: Array.isArray(luckyWinners) ? luckyWinners : [],
    isLoading: Boolean(loadingByFilter[activeFilter])
  }
}

export default useTopPerformance
